import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { CalendarIcon, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Task, TaskCreate, TaskUpdate, TaskStatus, TaskPriority, EntityReference } from '@/app/types/task';
import { createTask, updateTask } from '@/app/services/taskService';
import { listUsers } from '@/app/services/userService';
import { useOrganization } from '@/app/hooks/useOrganization';

const taskFormSchema = z.object({
  title: z.string().min(1, 'Title is required').max(255, 'Title must be less than 255 characters'),
  description: z.string().optional(),
  priority: z.nativeEnum(TaskPriority),
  status: z.nativeEnum(TaskStatus),
  task_category: z.string().optional(),
  task_type: z.string().optional(),
  due_date: z.date().optional(),
  assigned_to_user_id: z.number().optional(),
});

type TaskFormValues = z.infer<typeof taskFormSchema>;

interface TaskFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  task?: Task | null;
  parentEntity?: EntityReference;
  relatedEntity?: EntityReference;
  onSuccess?: (task: Task) => void;
  defaultValues?: Partial<TaskFormValues>;
}

const TASK_CATEGORIES = [
  'Service',
  'Body Work',
  'Insurance',
  'Warranty',
  'Tyres & Alignment',
  'AC & Electrical',
  'Detailing',
  'Customer Care',
  'Payment',
  'Other',
];

const TASK_TYPES = [
  { value: 'appointment', label: 'Appointment' },
  { value: 'service_reminder', label: 'Service Reminder' },
  { value: 'follow_up', label: 'Follow Up Call' },
  { value: 'insurance_renewal', label: 'Insurance Renewal' },
  { value: 'warranty_check', label: 'Warranty Check' },
  { value: 'vehicle_inspection', label: 'Vehicle Inspection' },
  { value: 'maintenance', label: 'Scheduled Maintenance' },
  { value: 'payment_followup', label: 'Payment Follow-up' },
  { value: 'feedback_call', label: 'Feedback Call' },
  { value: 'other', label: 'Other' },
];

export default function TaskForm({
  open,
  onOpenChange,
  task,
  parentEntity,
  relatedEntity,
  onSuccess,
  defaultValues,
}: TaskFormProps) {
  const queryClient = useQueryClient();
  const { currentSite } = useOrganization();
  const isEditMode = Boolean(task);

  // Fetch users for assignment dropdown
  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => listUsers({ limit: 100 }),
    enabled: open,
  });

  const form = useForm<TaskFormValues>({
    resolver: zodResolver(taskFormSchema),
    defaultValues: {
      title: '',
      description: '',
      priority: TaskPriority.MEDIUM,
      status: TaskStatus.OPEN,
      task_category: '',
      task_type: '',
      due_date: undefined,
      assigned_to_user_id: undefined,
      ...defaultValues,
    },
  });

  useEffect(() => {
    if (task) {
      form.reset({
        title: task.title,
        description: task.description || '',
        priority: task.priority,
        status: task.status,
        task_category: task.task_category || '',
        task_type: task.task_type || '',
        due_date: task.due_date ? new Date(task.due_date) : undefined,
        assigned_to_user_id: task.assigned_to_user_id || undefined,
      });
    } else if (defaultValues) {
      form.reset({
        ...form.getValues(),
        ...defaultValues,
      });
    }
  }, [task, defaultValues, form]);

  const createMutation = useMutation({
    mutationFn: (data: TaskCreate) => createTask(data),
    onSuccess: (newTask) => {
      toast.success('Task created successfully');
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      onSuccess?.(newTask);
      onOpenChange(false);
      form.reset();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to create task');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: TaskUpdate }) => updateTask(id, data),
    onSuccess: (updatedTask) => {
      toast.success('Task updated successfully');
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      onSuccess?.(updatedTask);
      onOpenChange(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to update task');
    },
  });

  const onSubmit = (values: TaskFormValues) => {
    if (isEditMode && task) {
      const updateData: TaskUpdate = {
        title: values.title,
        description: values.description,
        priority: values.priority,
        status: values.status,
        task_category: values.task_category,
        task_type: values.task_type || undefined,
        due_date: values.due_date ? values.due_date.toISOString() : undefined,
        assigned_to_user_id: values.assigned_to_user_id,
      };
      updateMutation.mutate({ id: task.id, data: updateData });
    } else {
      if (!parentEntity) {
        toast.error('Parent entity is required to create a task');
        return;
      }
      
      const createData: TaskCreate = {
        title: values.title,
        description: values.description,
        parent_entity_type: parentEntity.type,
        parent_entity_id: parentEntity.id,
        related_entity_type: relatedEntity?.type,
        related_entity_id: relatedEntity?.id,
        priority: values.priority,
        status: values.status,
        task_category: values.task_category,
        task_type: values.task_type || undefined,
        due_date: values.due_date ? values.due_date.toISOString() : undefined,
        assigned_to_user_id: values.assigned_to_user_id,
        site_id: currentSite?.id,
      };
      createMutation.mutate(createData);
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (open && e.key === 'Escape') {
        e.preventDefault();
        onOpenChange(false);
      }
      if (open && (e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        form.handleSubmit(onSubmit)();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, form, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{isEditMode ? 'Edit Task' : 'Create New Task'}</DialogTitle>
          <DialogDescription>
            {isEditMode 
              ? 'Update the task details below.' 
              : 'Fill in the details to create a new task.'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter task title" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Enter task description (optional)"
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Provide additional details or context for this task.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="priority"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Priority</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select priority" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value={TaskPriority.LOW}>Low</SelectItem>
                        <SelectItem value={TaskPriority.MEDIUM}>Medium</SelectItem>
                        <SelectItem value={TaskPriority.HIGH}>High</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value={TaskStatus.OPEN}>Open</SelectItem>
                        <SelectItem value={TaskStatus.COMPLETED}>Completed</SelectItem>
                        <SelectItem value={TaskStatus.CANCELLED}>Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="task_category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {TASK_CATEGORIES.map((category) => (
                          <SelectItem key={category} value={category}>
                            {category}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Categorize the task for better organization.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="task_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Task Type</FormLabel>
                    <Select onValueChange={(v) => field.onChange(v === '__none__' ? '' : v)} value={field.value || '__none__'}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="__none__">None</SelectItem>
                        {TASK_TYPES.map((tt) => (
                          <SelectItem key={tt.value} value={tt.value}>
                            {tt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="due_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Due Date</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value ? (
                              format(field.value, "PPP")
                            ) : (
                              <span>Pick a date</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          disabled={(date) =>
                            date < new Date(new Date().setHours(0, 0, 0, 0))
                          }
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormDescription>
                      When should this task be completed?
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="assigned_to_user_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Assign To</FormLabel>
                  <Select 
                    onValueChange={(value) => field.onChange(value === 'unassigned' ? undefined : Number(value))} 
                    value={field.value?.toString() || 'unassigned'}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a user (optional)" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {usersData?.items.map((user) => (
                        <SelectItem key={user.id} value={user.id.toString()}>
                          {user.full_name || user.username} ({user.username})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Assign this task to a specific user
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {parentEntity && (
              <div className="text-sm text-muted-foreground">
                This task will be linked to: {parentEntity.type} #{parentEntity.id}
                {relatedEntity && (
                  <span> (Related to: {relatedEntity.type} #{relatedEntity.id})</span>
                )}
              </div>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isEditMode ? 'Update Task' : 'Create Task'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}